<template>
  <Vue3DraggableResizable
    class="z-10 rounded"
    :class="[field.type.includes('display') ? 'display' : mode]"
    :parent="true"
    :resizable="isResizable"
    :draggable="isDraggable"
    :initW="w"
    :initH="h"
    v-model:w="w"
    v-model:h="h"
    v-model:x="x"
    v-model:y="y"
    :active="active"
    @activated="setActivated"
    @deactivated="setDeactivated"
    @drag-start="onDragStart"
    @drag-end="onDragEnd"
    @resize-end="onResizeEnd"
    class-name-active="draggable-active"
    ref="draggable"
    :id="id"
  >
    <div
      class="flex items-center w-full h-full rounded px-2 py-2 relative"
      :style="{
        background:
          mode !== 'display' &&
          !(mode === 'input' && ['text_display', 'mappable'].includes(field.type))
            ? 'rgba(22, 160, 133, 0.2)'
            : ''
      }"
      @click.stop="processClick"
      @mouseenter="hover = true"
      @mouseleave="hover = false"
    >
      <template v-if="field.type === 'text'">
        <template v-if="mode === 'edit'">
          <img src="@/assets/icons/text_field.svg" class="w-5 h-5 mr-2" />
          <div class="flex grow border rounded bg-white w-full h-full"></div>
        </template>

        <template v-if="mode === 'input'">
          <div class="flex items-center grow border rounded bg-white w-full h-full px-1">
            <p class="truncate" v-if="field.value">{{ field.value }}</p>
          </div>
        </template>

        <template v-if="mode === 'display'">
          <div class="flex items-center w-full h-full px-1">
            <p class="whitespace-pre" v-if="field.submissions && field.submissions.length">
              {{ `${field.submissions.map((s) => s.value).join("\n")}` }}
            </p>
          </div>
        </template>
      </template>

      <template v-if="field.type === 'text_display'">
        <template v-if="mode === 'edit'">
          <SingleLineInput
            :value="field.label"
            @input="handleTextDisplayInput"
            :expandable="true"
            ref="textDisplayInput"
          ></SingleLineInput>
        </template>
        <template v-if="['input', 'display'].includes(mode)">
          <p class="cursor-default" style="overflow-wrap: anywhere">{{ field.label }}</p>
        </template>
      </template>

      <template v-if="field.type === 'checkbox'">
        <template v-if="['edit', 'input'].includes(mode)">
          <div
            class="w-5 h-5 border-gray-300 rounded mx-auto flex items-center"
            :class="[value ? 'bg-brand-green' : 'bg-white']"
          >
            <CheckIcon class="stroke-2 w-5 h-5 text-white" v-if="value" />
          </div>
        </template>
        <template v-else>
          <div class="w-5 h-5 border-gray-300 rounded mx-auto flex items-center bg-transparent">
            <CheckIcon
              class="stroke-2 w-5 h-5 text-brand-black"
              v-if="field.submissions && field.submissions.length && field.submissions[0]"
            />
          </div>
        </template>
      </template>

      <template v-if="field.type === 'initial'">
        <PencilIcon v-if="mode !== 'display'" class="w-8 h-8 text-brand-green mr-2" />
        <template v-if="mode === 'edit'">
          <div class="flex grow border rounded bg-white w-full h-full"></div>
        </template>
        <template v-if="mode === 'input'">
          <div
            class="flex items-center grow border rounded bg-white w-full h-full px-1 cursor-pointer"
          >
            <p class="typed-signature text-lg truncate">{{ field.value }}</p>
          </div>
        </template>
        <template v-if="mode === 'display'">
          <div v-if="field.submissions && field.submissions.length" class="flex flex-col">
            <div
              v-for="(submission, idx) in field.submissions"
              :key="`${field.id}-submission-${idx}`"
              class="relative"
              @mouseenter="initialsHover[`${field.id}-submission-${idx}`] = true"
              @mouseleave="initialsHover[`${field.id}-submission-${idx}`] = false"
            >
              <div class="flex items-center w-full h-full px-1">
                <p class="typed-signature text-2xl">{{ submission.value }}</p>
              </div>

              <div
                class="flex rounded absolute left-0 -bottom-16 px-1 py-1"
                style="background: rgba(215, 222, 242, 0.5)"
                v-if="initialsHover[`${field.id}-submission-${idx}`]"
              >
                <p class="text-xs text-brand-blue">
                  Initialed by: <br />
                  {{ submission.username }} <br />
                  @ {{ formatTimestamp(submission.timestamp) }}
                </p>
              </div>
            </div>
          </div>
        </template>
      </template>

      <template v-if="field.type === 'signature'">
        <PencilIcon v-if="mode !== 'display'" class="w-8 h-8 text-brand-green mr-2" />

        <template v-if="mode === 'edit'">
          <div class="flex grow border rounded bg-white w-full h-full"></div>
        </template>

        <template v-if="mode === 'input'">
          <template v-if="field.value">
            <template v-if="field.signatureType === 'draw'">
              <div
                class="flex items-center grow border rounded bg-white w-full h-full px-1 cursor-pointer"
              >
                <img :src="field.signatureImgUrl" class="object-contain" />
              </div>
            </template>
            <template v-else>
              <div
                class="flex items-center grow border rounded bg-white w-full h-full px-1 cursor-pointer"
              >
                <p class="typed-signature text-lg truncate">{{ field.value }}</p>
              </div>
            </template>
          </template>

          <template v-else>
            <div class="flex grow border rounded bg-white w-full h-full"></div>
          </template>
        </template>

        <template v-if="mode === 'display'">
          <div v-if="field.submissions && field.submissions.length" class="flex flex-col">
            <div
              v-for="(submission, idx) in field.submissions"
              :key="`${field.id}-submission-${idx}`"
              class="relative"
              @mouseenter="signaturesHover[`${field.id}-submission-${idx}`] = true"
              @mouseleave="signaturesHover[`${field.id}-submission-${idx}`] = false"
            >
              <template v-if="submission.signatureType === 'draw'">
                <div class="flex items-center grow w-full h-full px-1">
                  <img :src="submission.signatureImgUrl" class="object-contain" />
                </div>
              </template>
              <template v-else>
                <div class="flex items-center w-full h-full px-1">
                  <p class="typed-signature text-2xl">{{ submission.value }}</p>
                </div>
              </template>

              <div
                class="flex rounded absolute -right-10 -bottom-16 px-1 py-1"
                style="background: rgba(215, 222, 242, 0.5)"
                v-if="signaturesHover[`${field.id}-submission-${idx}`]"
              >
                <p class="text-xs text-brand-blue">
                  Signed by: <br />
                  {{ submission.username }} <br />
                  @ {{ formatTimestamp(submission.timestamp) }}
                </p>
              </div>
            </div>
          </div>
        </template>
      </template>

      <template v-if="field.type === 'date'">
        <CalendarIcon v-if="mode !== 'display'" class="w-8 h-8 mr-2 text-brand-green" />
        <template v-if="mode === 'edit'">
          <div class="flex grow border rounded bg-white w-full h-full"></div>
        </template>
        <template v-if="mode === 'input'">
          <div class="flex items-center grow border rounded bg-white w-full h-full px-1">
            <p v-if="field.value">{{ field.value }}</p>
          </div>
        </template>

        <template v-if="mode === 'display'">
          <div class="flex items-center w-full h-full px-1">
            <p v-if="field.submissions && field.submissions.length">
              {{ field.submissions[0].value }}
            </p>
          </div>
        </template>
      </template>

      <template v-if="field.type === 'mappable'">
        <ChevronDoubleRightIcon v-if="mode === 'edit'" class="w-8 h-8 mr-2 text-brand-green" />
        <template v-if="mode === 'edit'">
          <p class="text-brand-green text-sm cursor-default">{{ field.field_label }}</p>
        </template>
        <template v-if="mode === 'input'">
          <p v-if="field.field_value" class="text-sm cursor-default whitespace-pre">
            {{ field.field_value }}
          </p>
        </template>
        <template v-if="mode === 'display'">
          <div class="flex items-center w-full h-full px-1">
            <p v-if="field.field_value" class="cursor-default whitespace-pre">
              {{ field.field_value }}
            </p>
          </div>
        </template>
      </template>
    </div>

    <template v-if="mode !== 'display' && !['text_display'].includes(field.user_type) && selected">
      <div class="flex items-center py-1">
        <p class="text-sm text-brand-green capitalize whitespace-nowrap">
          For: {{ field.user_type }}
        </p>
      </div>
    </template>

    <template v-if="mode === 'edit' && selected">
      <div
        class="rounded-full flex items-center justify-center absolute -right-5 -top-5 py-0.5 px-0.5 cursor-pointer hover:shadow"
        @click.stop="removeField"
        style="background: rgba(22, 160, 133, 0.2)"
      >
        <XIcon class="text-brand-gray w-5 h-5" />
      </div>
    </template>
  </Vue3DraggableResizable>

  <template v-if="showModal">
    <InputModal :showModal="showModal" @close="toggleModal" :field="field" />
  </template>
</template>
<script>
import {
  PencilIcon,
  CalendarIcon,
  CheckIcon,
  XIcon,
  ChevronDoubleRightIcon
} from "@heroicons/vue/outline"

import InputModal from "./InputModal.vue"
import moment from "moment"

export default {
  components: {
    PencilIcon,
    CalendarIcon,
    CheckIcon,
    InputModal,
    XIcon,
    ChevronDoubleRightIcon
  },
  computed: {
    isResizable() {
      if (["input", "display"].includes(this.mode)) {
        return false
      }
      return true
    },
    isDraggable() {
      if (["input", "display"].includes(this.mode)) {
        return false
      }
      return true
    },
    positionalData() {
      const { x, y, w, h } = this
      return { x, y, w, h }
    }
  },
  props: {
    id: {
      type: String
    },
    mode: {
      type: String,
      default: "edit" //input, display
    },
    active: {
      type: Boolean,
      default: false
    },
    field: {
      type: Object
    }
  },
  created() {
    const { x, y, w, h } = this.field
    this.x = x
    this.y = y
    this.w = w
    this.h = h
  },
  mounted() {},
  data() {
    return {
      x: 100,
      y: 100,
      w: 100,
      h: 100,
      value: "",
      showModal: false,
      signatureImgUrl: "",
      data: null,
      hover: false,
      signaturesHover: {},
      initialsHover: {},
      selected: false
    }
  },
  emits: [
    "drag-start",
    "drag-end",
    "resize-end",
    "activated",
    "deactivated",
    "set-value",
    "set-field",
    "remove"
  ],
  methods: {
    handleTextDisplayInput(e) {
      const el = this.$refs.textDisplayInput

      if (el.$el.offsetHeight > this.h) {
        this.h = el.$el.offsetHeight + 8
      }

      this.$emit("set-value", e.target.value)
    },
    // handleModalUpdate(data) {},
    toggleModal(data) {
      this.showModal = !this.showModal
      if (!this.showModal) {
        this.$emit("set-field", data)
      }
    },
    removeField() {
      this.$emit("remove")
    },
    setActivated() {
      this.selected = true
      this.$emit("activated")
    },
    setDeactivated() {
      this.selected = false
      this.$emit("deactivated")
    },
    onResizeEnd() {
      this.w = this.$refs.draggable.w
      this.h = this.$refs.draggable.h
      this.$emit("resize-end", this.positionalData)
    },
    onDragStart() {
      this.$emit("drag-start")
    },
    onDragEnd() {
      this.x = this.$refs.draggable.x
      this.y = this.$refs.draggable.y
      this.$emit("drag-end", this.positionalData)
    },
    processClick() {
      this.setActivated()

      if (this.mode === "edit") {
        return
      }

      if (this.mode === "input") {
        // open modals or set values
        if (["text_display", "mappable"].includes(this.field.type)) {
          return
        }
        if (this.field.type === "checkbox") {
          this.value = !this.value
          this.$emit("set-field", { value: this.value, ...this.field })
        } else {
          this.toggleModal()
        }
      }
    },
    formatTimestamp(timestamp) {
      return moment.unix(timestamp).format("MM/DD/YYYY hh:mm:ss")
    }
  }
}
</script>
<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Yellowtail&display=swap");

.draggable-active {
  border: none;
  box-shadow: var(--tw-ring-inset) 0 0 0 calc(4px + var(--tw-ring-offset-width)) #16a085;
}

.draggable-active.input {
  border: none;
  box-shadow: var(--tw-ring-inset) 0 0 0 calc(4px + var(--tw-ring-offset-width)) #365ac0;
}

.draggable-active.display {
  border: none;
  box-shadow: none;
}

.typed-signature {
  font-family: "Yellowtail", cursive;
  line-height: 1.5rem;
}
</style>
